/**
 * Dealer's stand-in. The launcher itself (dealer/*.js and command-palette.js) is large, and most page views
 * never open it, so every page loads only this: it answers Cmd/Ctrl+K and the Dealer openers,
 * offers window.wyldcardsDealer and the WebMCP tools, and fetches the real launcher on the
 * first of them — or ahead of time, when a pointer or focus reaches an opener or the page is idle.
 * Once the launcher has loaded it replaces window.wyldcardsDealer and wires its own openers,
 * and everything here stands aside.
 */
(function () {
    'use strict';

    if (window.wyldcardsDealer) return;

    /* Focus HUD replaces the auto-open Studio default once per device. A later
       explicit Dock choice is kept, including its last side and open state. */
    try {
        if (window.localStorage.getItem('wyldcards.focus-hud') !== '1') {
            window.localStorage.setItem('wyldcards.dealer.dock', 'null');
            window.localStorage.setItem('wyldcards.focus-hud', '1');
        }
    } catch (error) { /* The floating launcher also works without storage. */ }

    var OPENERS = '[data-command-palette-open], [data-command-palette-search], [data-dealer-inline-input], [data-dealer-copilot-toggle]';
    var IDLE_WAIT = 4000;
    var script = document.currentScript;
    /**
     * The launcher's files, in the order they run: its modules (the shared ranking, what typed
     * words mean, row markup), then the launcher itself, which replaces window.wyldcardsDealer.
     * sw.js precaches the same list (scripts/test-dealer-agents.cjs checks). They download side by side and run in this order.
     */
    var LAUNCHER = ['dealer/ranking.js', 'dealer/text.js', 'dealer/markup.js', 'dealer/voice.js', 'dealer/dock.js', 'dealer/copilot.js', 'dealer/replace.js', 'dealer/paste.js', 'dealer/insert.js', 'dealer/goto.js', 'dealer/recent.js', 'dealer/review.js', 'dealer/offline.js', 'dealer/hints.js', 'dealer/help.js', 'dealer/page.js', 'command-palette.js'];
    /* The same folder and ?v= as this file, so a new build fetches a new launcher. */
    function srcOf(name) {
        return script && script.src ? script.src.replace(/dealer\.js(?=$|\?)/, name) : '/js/' + name;
    }
    var loading = null;
    /* A search box being typed into while the launcher loads: its letters open the launcher. */
    var handoff = null;
    /* An open asked for while the launcher loads; a second Cmd+K meanwhile adds nothing. */
    var opening = false;

    function loaded() {
        return window.wyldcardsDealer !== stub;
    }

    function hasLauncher() {
        return !!document.getElementById('command-palette');
    }

    function load() {
        if (loaded()) return Promise.resolve(window.wyldcardsDealer);
        if (loading) return loading;
        loading = new Promise(function (resolve, reject) {
            var tags = LAUNCHER.map(function (name, index) {
                var tag = document.createElement('script');
                tag.src = srcOf(name);
                /* Added by script, async=false still downloads at once but runs the files in order. */
                tag.async = false;
                tag.onerror = function () {
                    loading = null;
                    tags.forEach(function (added) { added.remove(); });
                    reject(new Error('Could not load Dealer.'));
                };
                if (index === LAUNCHER.length - 1) {
                    tag.onload = function () {
                        if (loaded()) resolve(window.wyldcardsDealer);
                        else reject(new Error('Dealer did not start.'));
                    };
                }
                return tag;
            });
            tags.forEach(function (tag) { document.head.appendChild(tag); });
        });
        return loading;
    }

    function failed() {
        if (window.wyldcardsToast) window.wyldcardsToast('Could not open Dealer. Check your connection and try again.', { kind: 'error' });
    }

    function open(query) {
        if (opening) return;
        opening = true;
        load().then(function (dealer) {
            opening = false;
            if (handoff) {
                query = handoff.value;
                handoff.value = '';
                handoff = null;
            }
            dealer.open(query);
        }).catch(function () {
            opening = false;
            handoff = null;
            failed();
        });
    }

    function later(call) {
        return function () {
            var args = arguments;
            return load().then(function (dealer) { return dealer[call].apply(dealer, args); });
        };
    }

    var stub = {
        open: open,
        close: function () { if (loaded()) window.wyldcardsDealer.close(); },
        isOpen: function () { return false; },
        standIn: true,
        list: later('list'),
        search: later('search'),
        run: later('run'),
        repeat: later('repeat'),
        insert: later('insert')
    };
    window.wyldcardsDealer = stub;

    document.addEventListener('keydown', function (event) {
        if (loaded() || !hasLauncher() || event.isComposing || event.keyCode === 229) return;
        if ((event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === 'k') {
            event.preventDefault();
            if (!event.shiftKey) {
                open();
                return;
            }
            /* Cmd+Shift+K repeats the last command: the launcher answers the same keys once it is here. */
            var keys = { key: event.key, metaKey: event.metaKey, ctrlKey: event.ctrlKey, shiftKey: true, bubbles: true };
            load().then(function () { document.dispatchEvent(new KeyboardEvent('keydown', keys)); }).catch(failed);
            return;
        }
        var input = event.target.closest && event.target.closest('[data-command-palette-search], [data-dealer-inline-input]');
        if (!input || event.metaKey || event.ctrlKey || event.altKey) return;
        /* Letters type into the box as usual until the launcher can take them over. */
        if (event.key === 'Enter' || event.key === 'ArrowDown') event.preventDefault();
        else if (event.key.length !== 1) return;
        if (!handoff) {
            handoff = input;
            open();
        }
    }, true);

    /* A first paste can arrive before the lazy launcher. Keep clipboard data now,
       while the browser still exposes it, then replay it through the real field. */
    document.addEventListener('paste', function (event) {
        if (loaded() || !hasLauncher() || !document.getElementById('command-search') || !event.clipboardData) return;
        var input = event.target.closest && event.target.closest('[data-dealer-inline-input]');
        if (!input) return;
        var value = input.value;
        var start = input.selectionStart;
        var end = input.selectionEnd;
        if ((value.slice(0, start) + value.slice(end)).trim()) return;
        var text = event.clipboardData.getData('text/plain') || '';
        var files = Array.prototype.slice.call(event.clipboardData.files || []);
        var multiline = text.split(/\r\n|\r|\n/).filter(function (line) { return line.trim(); }).length > 1;
        if (!multiline && !files.some(function (file) { return /^image\//.test(file.type); })) return;
        event.preventDefault();
        event.stopPropagation();
        load().then(function (dealer) {
            if (!document.contains(input)) return;
            if (input.value !== value) {
                if (window.wyldcardsToast) window.wyldcardsToast('Dealer is ready. Paste again to add your cards.');
                return;
            }
            dealer.open(value);
            input.setSelectionRange(start, end);
            var paste = new Event('paste', { bubbles: true, cancelable: true });
            Object.defineProperty(paste, 'clipboardData', { value: {
                getData: function (type) { return type === 'text/plain' ? text : ''; }, files: files
            } });
            input.dispatchEvent(paste);
        }).catch(failed);
    }, true);

    /* The words just before the caret in a text box or an editable element, for spotting "[[". */
    function textBeforeCaret(el) {
        if (typeof el.selectionStart === 'number' && el.value != null) return el.value.slice(0, el.selectionStart);
        var selection = window.getSelection();
        if (!selection || !selection.rangeCount) return '';
        var range = selection.getRangeAt(0);
        return range.startContainer.nodeType === 3 ? range.startContainer.textContent.slice(0, range.startOffset) : '';
    }

    /* "[[" typed in a card's text field opens Dealer to link to a card from there (not in styled
       paragraph fields, where [[ starts a script note, and not "[[[" or a paste). */
    document.addEventListener('input', function (event) {
        var el = event.target;
        if (event.inputType !== 'insertText' || event.data !== '[' || !hasLauncher() || !el || !el.closest) return;
        var part = el.closest('main[data-card-id] .part[data-part-kind="field"]');
        if (!part || part.querySelector('.part-paragraphs')) return;
        var before = textBeforeCaret(el);
        if (/\[\[$/.test(before) && !/\[\[\[$/.test(before)) window.wyldcardsDealer.insert({ trigger: 2, link: true });
    }, true);

    document.addEventListener('click', function (event) {
        if (loaded()) return;
        var opener = event.target.closest && event.target.closest(OPENERS);
        if (!opener) return;
        event.preventDefault();
        /* data-command-palette-open="?" opens on that prefix, such as Help's Find a feature. */
        var query = opener.hasAttribute('data-dealer-copilot-toggle') ? 'copilot' :
            ((opener.hasAttribute('data-command-palette-search') || opener.hasAttribute('data-dealer-inline-input')) ? opener.value : (opener.dataset.commandPaletteOpen || undefined));
        open(query);
    }, true);

    function warm(event) {
        if (!loaded() && event.target.closest && event.target.closest(OPENERS)) load().catch(function () { /* Tried again on open. */ });
    }
    document.addEventListener('pointerover', warm, { passive: true });
    document.addEventListener('focusin', warm);

    /* Dealer docked open on the last page opens again on this one, so it is wanted now. */
    function dockedOpen() {
        try {
            var dock = JSON.parse(window.localStorage.getItem('wyldcards.dealer.dock'));
            return !!(dock && dock.open);
        } catch (error) {
            return false;
        }
    }
    function workspaceReady() {
        if (hasLauncher() && dockedOpen()) load().catch(function () { /* Opened again on Cmd+K. */ });
    }
    workspaceReady();
    document.addEventListener('htmx:afterSwap', workspaceReady);
    window.addEventListener('resize', workspaceReady);

    /* A script handler chosen in Dealer on the last page: the launcher opens its editor here. */
    function gotoPending() {
        try {
            return !!window.sessionStorage.getItem('wyldcards.dealer.goto');
        } catch (error) {
            return false;
        }
    }
    if (gotoPending()) load().catch(function () { /* The card is open; its script is a click away. */ });

    /* Ready before the first Cmd+K on most pages, but after everything the page needs to show. */
    function preload() {
        var connection = navigator.connection;
        if (loaded() || !hasLauncher() || (connection && connection.saveData)) return;
        (window.requestIdleCallback || function (fn) { return window.setTimeout(fn, 1500); })(function () {
            load().catch(function () { /* Tried again on open. */ });
        }, { timeout: IDLE_WAIT });
    }
    if (document.readyState === 'complete') preload();
    else window.addEventListener('load', preload);

    /* Protocol handler: when opened via web+wyldcards:// (PWA protocol_handlers) or query params */
    function checkProtocolUrl() {
        try {
            var search = window.location && window.location.search;
            if (!search) return;
            var params = new URLSearchParams(search);
            var proto = params.get('open_protocol');
            var query = params.get('dealer_q') || params.get('dealer_query');
            var command = params.get('dealer_command');

            if (proto) {
                var decoded = decodeURIComponent(proto);
                var target = decoded.replace(/^web\+wyldcards:\/*/, '');
                try {
                    var parsed = new URL('https://' + target);
                    var host = parsed.hostname;
                    if (host === 'dealer' || host === 'quickdraw') {
                        query = parsed.searchParams.get('q');
                        command = parsed.searchParams.get('command');
                    } else if (host === 'stack') {
                        var pathParts = parsed.pathname.split('/').filter(Boolean);
                        var stackUid = pathParts[0];
                        if (stackUid) {
                            var cardId = (pathParts.length >= 3 && pathParts[1] === 'card') ? pathParts[2] : null;
                            cleanProtocolParam();
                            window.location.href = '/stack/' + encodeURIComponent(stackUid) + (cardId ? ('/card/' + encodeURIComponent(cardId)) : '');
                            return;
                        }
                    } else if (host === 'search') {
                        var q = parsed.searchParams.get('q');
                        cleanProtocolParam();
                        window.location.href = '/search' + (q ? ('?q=' + encodeURIComponent(q)) : '');
                        return;
                    }
                } catch (err) {
                    var qMatch = /[?&]q=([^&]+)/.exec(decoded);
                    var cmdMatch = /[?&]command=([^&]+)/.exec(decoded);
                    if (qMatch) query = decodeURIComponent(qMatch[1].replace(/\+/g, ' '));
                    if (cmdMatch) command = decodeURIComponent(cmdMatch[1]);
                }
            }

            if (query !== null && query !== undefined) {
                cleanProtocolParam();
                open(query);
            } else if (command) {
                cleanProtocolParam();
                load().then(function (dealer) {
                    if (dealer && typeof dealer.run === 'function') dealer.run(command);
                }).catch(function () {});
            }
        } catch (e) {
            /* Ignore protocol handling error */
        }
    }

    function cleanProtocolParam() {
        try {
            var url = new URL(window.location.href);
            var changed = false;
            ['open_protocol', 'dealer_q', 'dealer_query', 'dealer_command'].forEach(function (k) {
                if (url.searchParams.has(k)) {
                    url.searchParams.delete(k);
                    changed = true;
                }
            });
            if (changed && window.history && window.history.replaceState) {
                var clean = url.pathname + (url.search ? url.search : '') + url.hash;
                window.history.replaceState(null, '', clean);
            }
        } catch (e) {}
    }

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', checkProtocolUrl);
    else checkProtocolUrl();


    /**
     * WebMCP: where the browser offers navigator.modelContext, the launcher's calls become tools
     * an assistant can use on the page. They go through the public API, never around it, so they
     * load the launcher when first used.
     */
    function registerAgentTools() {
        var context = window.navigator && window.navigator.modelContext;
        if (!context || typeof context.registerTool !== 'function') return;
        function tool(name, description, properties, required, readOnly, call) {
            try {
                context.registerTool({
                    name: name,
                    description: description,
                    inputSchema: { type: 'object', properties: properties, required: required },
                    annotations: { readOnlyHint: readOnly },
                    execute: function (input) {
                        return call(input || {}).then(function (result) {
                            return { content: [{ type: 'text', text: JSON.stringify(result) }] };
                        });
                    }
                });
            } catch (error) {
                /* An unfinished or changed WebMCP implementation: the launcher works without it. */
            }
        }
        tool('wyldcards_list_commands', 'List every command available on this Wyldcards page: stack actions, page controls, widgets, stacks to open, and app destinations. Each has an id for wyldcards_run_command.',
            {}, [], true, function () { return window.wyldcardsDealer.list(); });
        tool('wyldcards_search', 'Search Wyldcards commands, stacks, and the text of the signed-in user\'s cards. Results are ranked; card results carry an href to open.',
            { query: { type: 'string', description: 'Words to search for.' } }, ['query'], true,
            function (input) { return window.wyldcardsDealer.search(input.query); });
        tool('wyldcards_run_command', 'Run a Wyldcards command by the id that wyldcards_list_commands or wyldcards_search returned. Commands marked destructive delete or discard something; confirm with the user first.',
            { id: { type: 'string', description: 'The command id, such as "stack.new-card".' } }, ['id'], false,
            function (input) { return window.wyldcardsDealer.run(input.id); });
    }

    registerAgentTools();
})();
